// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { FormHelperText, Box } from '@mui/material';
// dnd
import { DragDropContext, Droppable, Draggable } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
// type
import {
  UploadAvatar,
  Upload,
  UploadBox,
  UploadProps,
  UploadMultiFileProps,
} from '../upload';
import { withImageSize } from 'src/utils/optimizedImageUrl';
import { useCallback, useState, useMemo, useEffect } from 'react';
import { ExtendFile } from '../file-thumbnail/types';

// ----------------------------------------------------------------------

interface Props extends Omit<UploadProps, 'file'> {
  name: string;
  maxWidth?: number;
  maxHeight?: number;
}

export function RHFUploadAvatar({ name, ...other }: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && !field.value;

        return (
          <div>
            <UploadAvatar
              accept={{ 'image/*': [] }}
              error={checkError}
              {...other}
              file={field.value}
            />
            {checkError && (
              <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                {error.message}
              </FormHelperText>
            )}
          </div>
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUploadBox({ name, ...other }: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <UploadBox files={field.value} error={!!error} {...other} />
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUpload({ name, multiple, helperText, maxWidth, maxHeight, ...other }: Props) {
  const { control, setValue, getValues } = useFormContext();
  const [sizeError, setSizeError] = useState<string | null>(null);

  const onDragMove = useCallback(
    (reorderedFiles?: Array<{ id: string; file: ExtendFile | string }>) => {
      const newFiles = reorderedFiles?.map(({ file }) => file) || [];

      setValue(name, newFiles, { shouldValidate: true });
    },
    [setValue, name]
  );

    const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const validateImageSize = (file: File): Promise<boolean> => {
        if (!maxWidth && !maxHeight) {
          return Promise.resolve(true);
        }
        
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const isValidWidth = !maxWidth || img.width <= maxWidth;
            const isValidHeight = !maxHeight || img.height <= maxHeight;
            const isValid = isValidWidth && isValidHeight;
            resolve(isValid);
          };
          img.onerror = () => resolve(false);
          img.src = URL.createObjectURL(file);
        });
      };

      const processFiles = async () => {
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];
        
        for (const file of acceptedFiles) {
          const isValidSize = await validateImageSize(file);
          if (isValidSize) {
            validFiles.push(file);
          } else {
            const sizeText = maxWidth && maxHeight 
              ? `${maxWidth}x${maxHeight}px`
              : maxWidth 
                ? `ширину ${maxWidth}px`
                : `высоту ${maxHeight}px`;
            invalidFiles.push(`${file.name} (${sizeText})`);
          }
        }

        if (invalidFiles.length > 0) {
          setSizeError(`Файлы превышают допустимые размеры: ${invalidFiles.join(', ')}`);
        } else {
          setSizeError(null);
        }

        const newFiles = validFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );

        if (multiple) {
          const currentFiles = getValues(name) || [];
          setValue(name, [...currentFiles, ...newFiles], { shouldValidate: true });
        } else {
          setValue(name, newFiles[0], { shouldValidate: true });
        }
      };

      processFiles();
    },
    [setValue, getValues, name, multiple, maxWidth, maxHeight]
  );

  const onRemove = useCallback(
    (inputFile: string | ExtendFile) => {
      const currentFiles = getValues(name) || [];

      setValue(
        name,
        currentFiles.filter((file) => file !== inputFile),
        { shouldValidate: true }
      );
    },
    [setValue, getValues, name]
  );

  const onRemoveAll = useCallback(
    () => setValue(name, [], { shouldValidate: true }),
    [setValue, name]
  );

  const handles = {
    onDragMove,
    onDrop,
    onRemove,
    onRemoveAll,
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) =>
        multiple ? (
          <Upload
            multiple
            accept={{ 'image/*': [] }}
            files={field.value}
            error={!!error || !!sizeError}
            helperText={
              (!!error || helperText || sizeError) && (
                <FormHelperText error={!!error || !!sizeError} sx={{ px: 2 }}>
                  {error ? error?.message : sizeError || helperText}
                </FormHelperText>
              )
            }
            {...handles}
            {...other}
          />
        ) : (
          <Upload
            accept={{ 'image/*': [] }}
            file={field.value}
            error={!!error || !!sizeError}
            helperText={
              (!!error || helperText || sizeError) && (
                <FormHelperText error={!!error || !!sizeError} sx={{ px: 2 }}>
                  {error ? error?.message : sizeError || helperText}
                </FormHelperText>
              )
            }
            {...handles}
            {...other}
          />
        )
      }
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUploadSingleFile({ name, ...other }: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && !field.value;

        return (
          <Upload
            accept={{ 'image/*': [] }}
            file={field.value}
            error={checkError}
            helperText={
              checkError && (
                <FormHelperText error sx={{ px: 2 }}>
                  {error.message}
                </FormHelperText>
              )
            }
            {...other}
          />
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

interface RHFUploadMultiFileProps extends Omit<UploadMultiFileProps, 'files'> {
  name: string;
}

export function RHFUploadMultiFile({ name, ...other }: RHFUploadMultiFileProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && field.value?.length === 0;

        return (
          <Upload
            multiple
            accept={{ 'image/*': [] }}
            files={field.value}
            error={checkError}
            helperText={
              checkError && (
                <FormHelperText error sx={{ px: 2 }}>
                  {error?.message}
                </FormHelperText>
              )
            }
            {...other}
          />
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFImportCSVFile({ name, ...other }: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && !field.value;

        return (
          <Upload
            accept={{ 'text/csv': ['.csv'] }}
            file={field.value}
            error={checkError}
            helperText={
              checkError && (
                <FormHelperText error sx={{ px: 2 }}>
                  {error.message}
                </FormHelperText>
              )
            }
            {...other}
          />
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

interface RHFImageSelectorProps {
  name: string;
  sourceImages?: string[];
  title?: string;
  subtitle?: string;
  disabled?: boolean;
}

export function RHFImageSelector({ 
  name, 
  sourceImages = [], 
  title = "Доступные изображения", 
  subtitle = "Выбранные изображения",
  disabled = false 
}: RHFImageSelectorProps) {
  const { control, setValue, watch } = useFormContext();
  const watchedImages = watch(name);
  
  // Мемоизируем selectedImages чтобы избежать пересоздания на каждом рендере
  const selectedImages = useMemo(() => watchedImages || [], [watchedImages]);

  // Подготавливаем данные для drag-and-drop
  const draggableImages = useMemo(() => {
    if (!Array.isArray(selectedImages)) {
      return [];
    }
    return selectedImages.map((url: string, index: number) => ({
      id: `image-${url}-${index}`,
      url,
    }));
  }, [selectedImages]);

  const [localImages, setLocalImages] = useState(draggableImages);

  useEffect(() => {
    setLocalImages(draggableImages);
  }, [draggableImages]);

  // Обработчик завершения drag-and-drop
  const handleDragEnd = useCallback((result: any) => {
    if (!result.destination || disabled) {
      return;
    }

    const reorderedImages = Array.from(localImages);
    const [removed] = reorderedImages.splice(result.source.index, 1);
    reorderedImages.splice(result.destination.index, 0, removed);

    setLocalImages(reorderedImages);

    // Обновляем форму
    const newImageUrls = reorderedImages.map(item => item.url);
    setValue(name, newImageUrls, { shouldDirty: true, shouldValidate: true });
  }, [localImages, setValue, name, disabled]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box>
          {/* Доступные изображения для выбора */}
          {sourceImages.length > 0 && (
            <Box>
              <Box fontWeight={600} mb={2}>{title}</Box>
              <Box display="flex" flexWrap="wrap" gap={1.5} mb={3}>
                {sourceImages.map((url: string) => {
                  const selected = Array.isArray(selectedImages) && selectedImages.includes(url);
                  return (
                    <Box 
                      key={url} 
                      sx={{ 
                        position: 'relative', 
                        width: 96, 
                        height: 96, 
                        borderRadius: 1, 
                        overflow: 'hidden', 
                        boxShadow: selected ? '0 0 0 2px #12B76A inset' : '0 0 0 1px #e5e7eb inset', 
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.6 : 1
                      }}
                      onClick={() => {
                        if (disabled) return;
                        const current: string[] = Array.isArray(selectedImages) ? selectedImages : [];
                        const next = selected 
                          ? current.filter(i => i !== url) 
                          : Array.from(new Set([...current, url]));
                        setValue(name, next, { shouldDirty: true, shouldValidate: true });
                      }}
                      title={disabled ? 'Заблокировано' : (selected ? 'Убрать' : 'Добавить')}
                    >
                      <img src={withImageSize(url, 'md')} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Выбранные изображения с возможностью перетаскивания */}
          <Box>
            <Box fontWeight={600} mb={2}>{subtitle}</Box>
            
            {localImages.length > 0 ? (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="selected-images" direction="horizontal">
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        minHeight: 96,
                      }}
                    >
                      {localImages.map((item, index) => (
                        <Draggable 
                          key={item.id} 
                          draggableId={item.id} 
                          index={index}
                          isDragDisabled={disabled}
                        >
                          {(provided) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                position: 'relative',
                                width: 96,
                                height: 96,
                                borderRadius: 1,
                                overflow: 'hidden',
                                boxShadow: '0 0 0 1px #e5e7eb inset',
                                cursor: disabled ? 'not-allowed' : 'grab',
                                opacity: disabled ? 0.6 : 1,
                                ...provided.draggableProps.style,
                              }}
                            >
                              <img 
                                src={withImageSize(item.url, 'md')} 
                                alt="" 
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover',
                                  pointerEvents: 'none'
                                }} 
                              />
                              
                              {/* Индикатор порядка */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  left: 4,
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  background: 'rgba(0,0,0,0.7)',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 12,
                                  fontWeight: 'bold',
                                  pointerEvents: 'none'
                                }}
                              >
                                {index + 1}
                              </Box>
                              
                              {/* Кнопка удаления */}
                              {!disabled && (
                                <Box
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setValue(name, (selectedImages as string[]).filter(i => i !== item.url), { shouldDirty: true, shouldValidate: true });
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    background: '#fff',
                                    boxShadow: '0 1px 4px rgba(0,0,0,.15)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    lineHeight: 1,
                                    color: '#111'
                                  }}
                                  title="Удалить"
                                >
                                  ×
                                </Box>
                              )}
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <Box sx={{ color: 'text.secondary', py: 2 }}>Нет выбранных изображений</Box>
            )}
          </Box>

          {/* Ошибка валидации */}
          {error && (
            <FormHelperText error sx={{ mt: 1 }}>
              {error.message}
            </FormHelperText>
          )}
        </Box>
      )}
    />
  );
}
